import React, { useCallback, useMemo, useState } from 'react';
import { CustomToggle } from './CustomToggle';
import { Dropdown as BSDropdown } from 'react-bootstrap';
import Tippy from '@tippyjs/react';
import { SearchInput } from '../SearchInput/SearchInput';

import './Dropdown.scss';

interface DropDownProps {
  label?: string;
  multiselect?: boolean;
  showSelectBox?: boolean;
  options: { label: string; value: string; icon?: React.ReactNode; disabled?: boolean; tooltip?: string; searchText?: string }[];
  currentValue: string | string[];
  onUpdate: (newValue: string) => void;
  variant?: 'primary' | 'transparent' | 'rounded' | 'no-border';
  hideLabel?: boolean;
  toggleIcon?: React.ReactNode;
  customOption?: React.ReactNode;
  fullwidth?: boolean;
  searchable?: boolean;
}

export const Dropdown = ({
  label,
  options,
  currentValue,
  onUpdate,
  variant = 'primary',
  hideLabel = false,
  multiselect = false,
  toggleIcon,
  customOption,
  showSelectBox = false,
  fullwidth = false,
  searchable = false,
}: DropDownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const currentValueObj = useCallback(
    () => options.find((opt) => opt.value === currentValue) || { label: currentValue, icon: undefined },
    [options, currentValue]
  );

  const visibleOptions = useMemo(() => {
    if (!searchable || !search.trim()) return options;
    const q = search.trim().toLowerCase();
    return options.filter((opt) => (opt.searchText ?? opt.label).toLowerCase().includes(q));
  }, [options, search, searchable]);

  const onToggle = (next: boolean) => {
    setIsOpen(next);
    if (!next) setSearch('');
  };

  return (
    <BSDropdown show={isOpen} onToggle={onToggle}>
      <BSDropdown.Toggle as={CustomToggle} id="dropdown-custom-components" variant={variant} toggleIcon={toggleIcon} fullwidth={fullwidth}>
        {!hideLabel && !multiselect && currentValueObj().icon}
        {!hideLabel && label && `${label} `}
        {!hideLabel && !multiselect && currentValueObj().label}
      </BSDropdown.Toggle>

      <BSDropdown.Menu className={`new-dropdown__list new-dropdown__list${fullwidth ? '--fullwidth' : ''}`}>
        {searchable && (
          <div className="new-dropdown__search" onClick={(e) => e.stopPropagation()}>
            <SearchInput searchInput={search} setSearchInput={setSearch} />
          </div>
        )}
        {visibleOptions.map((opt) => {
          const isActive = multiselect ? (Array.isArray(currentValue) ? currentValue.includes(opt.value) : false) : opt.value === currentValue;

          const item = (
            <BSDropdown.Item
              key={opt.value}
              disabled={opt.disabled}
              className={`new-dropdown__list--item${isActive ? ' active' : ''} ${multiselect || showSelectBox ? 'space-between' : ''}`}
              eventKey={opt.value}
              onClick={(e) => {
                if (opt.disabled) return;
                e.stopPropagation();
                !multiselect && setIsOpen(false);
                onUpdate(opt.value);
              }}
            >
              {opt.icon && opt.icon}
              {opt.label}
              {(multiselect || showSelectBox) && (
                <div className={`new-dropdown__${multiselect ? 'multiselect' : 'singleselect-outer'}`}>
                  {isActive && multiselect && <i className="fa-regular fa-check" />}
                  {isActive && !multiselect && <span className="new-dropdown__singleselect-inner" />}
                </div>
              )}
            </BSDropdown.Item>
          );

          if (opt.tooltip) {
            return (
              <Tippy key={opt.value} content={<p className="gaby-tooltip arrow-bottom">{opt.tooltip}</p>}>
                <div>{item}</div>
              </Tippy>
            );
          }

          return item;
        })}
        {customOption && (
          <BSDropdown.Item eventKey="custom" className="new-dropdown__list--item item-custom">
            {customOption}
          </BSDropdown.Item>
        )}
      </BSDropdown.Menu>
    </BSDropdown>
  );
};
